import {
  GAP_PX,
  MIN_READABLE_CHART_PX,
  PAIRING_STEPS,
} from "@/components/runs/torsion-fit-pairing";

/**
 * **Trading width between the main area and the rail** (flowstack#991 cause 3).
 *
 * The operator's report was that the workspace reads as "two unrelated boxes":
 * two separate rounded cards with a gutter between them and nothing saying they
 * are one surface. The recommendation was one bordered surface split by a
 * **draggable divider** — *"a drag handle is self-explaining: it says the two
 * panes are one workspace and lets a user trade space between DAG and
 * visuals"*.
 *
 * **The handle is taken; the single border is not, and that is a judgement
 * rather than an omission.** Since #991 was written the panel chrome became a
 * contract rather than this workspace's own markup: `VisualPanel` / VIS/V0
 * decided ONE form for every scientific visual in the portal, and the rail
 * became a STACK of panels rather than one pane (VIS/V6) — the molecule, the
 * step's settings, the fit numbers, each its own container with its own
 * maximise. "One bordered surface split by a divider" is a shape for two panes.
 * With four it would mean either dissolving the container contract on this one
 * surface, or drawing a border around a group whose members already draw their
 * own. What the recommendation was FOR — the two halves reading as one
 * workspace the user apportions — is what the handle delivers, and it delivers
 * it for a stack as well as for a pair.
 *
 * ---
 *
 * ## The two numbers this module exists to hold apart
 *
 * The rail's width is a LADDER by default (`torsion-fit-pairing.ts`): 18rem
 * from `pair`, 22rem from `xl`, 26rem from `2xl`, chosen so the main pane never
 * drops below {@link MIN_READABLE_CHART_PX}. That floor is not a preference —
 * it is the width at which a dihedral curve's axis labels, legend and two
 * co-plotted series stay distinguishable, and the whole ladder is derived from
 * it. **A drag must not be allowed to cross it**, so the user's number is
 * clamped rather than obeyed: {@link railSplitBounds} turns the row's own width
 * into the interval the divider may move in, and returns `null` for a row too
 * narrow to have one — at which point no divider renders at all, because a
 * control that cannot move is chrome pretending to be a choice.
 *
 * Kept as pure functions over pixel widths, with no DOM and no React, so the
 * clamping law is testable without a layout engine — jsdom has none, and the
 * arithmetic is the part that can be wrong.
 */

/**
 * The narrowest the rail may be dragged.
 *
 * Below roughly this the rail stops being a readable detail column: the tile
 * settings are a `grid-cols-[auto_1fr]` definition list of run ids and content
 * hashes, and the fit tiles print `0.597 kcal/mol` in tabular figures. It sits
 * under the ladder's own first step (18rem = 288px) on purpose — the ladder is
 * where the rail RESTS, and the point of the handle is to go somewhere the
 * ladder would not put you.
 */
export const RAIL_MIN_PX = 240;

/**
 * The widest, independent of how much room the row has.
 *
 * The main pane's floor already stops the rail eating the graph on a normal
 * window; this stops the *opposite* absurdity on a very wide one, where
 * `rowWidth - GAP - 392` is over a thousand pixels and a rail dragged to it
 * would be a detail column wider than any of its content, beside a graph
 * squeezed to its minimum. 40rem is a little over the ladder's widest step.
 */
export const RAIL_MAX_PX = 640;

/** One press of an arrow key. Small enough to place the divider precisely,
 * large enough that crossing the ladder's 4rem steps is not a hundred presses. */
export const RAIL_KEY_STEP_PX = 16;

/**
 * The viewport width at and above which the workspace is a ROW — the same
 * `pair` breakpoint the rail's ladder starts at, read from the ladder rather
 * than restated, so the divider cannot appear at a width where the layout has
 * stacked and there is no horizontal split to drag.
 */
export const SPLIT_MIN_VIEWPORT_PX = PAIRING_STEPS[0].minWidthPx;

/** The media query that answers "is the workspace side by side right now". */
export const SPLIT_MEDIA_QUERY = `(min-width: ${SPLIT_MIN_VIEWPORT_PX}px)`;

export interface RailSplitBounds {
  min: number;
  max: number;
}

/**
 * How far the divider may travel in a row of this width, or `null` when it may
 * not travel at all.
 *
 * `rowWidth - GAP_PX - MIN_READABLE_CHART_PX` is the rail width at which the
 * main pane is exactly at its floor; anything wider takes the curve below it.
 * `GAP_PX` is subtracted once and only once because the divider REPLACES the
 * row's gap rather than adding to it (see `RailSplitter`) — the space between
 * the two panes is 16px with the divider exactly as it is without one, which is
 * what keeps `mainPaneWidthPx`'s arithmetic (and the ladder derived from it)
 * true whether or not a divider is on screen.
 *
 * **The interval is one-sided at the narrow end, and that is the correct
 * answer rather than a degenerate one.** At `pair` the ladder's first step
 * already spends the whole margin — 288px of rail leaves the main pane at
 * exactly its 392px floor — so `max` lands ON the ladder's own width: the rail
 * can be given up to the graph there, and cannot be taken from it. That is a
 * handle worth rendering, because the direction that still works is the one a
 * user on a 960px window wants (more graph), and the direction that does not is
 * the one the floor forbids for a reason.
 *
 * `null` is reserved for a row with no interval at all — an unmeasured or
 * zero-width row, or one so narrow that even {@link RAIL_MIN_PX} would take the
 * curve under its floor. The caller renders no handle there rather than one
 * that cannot move.
 */
export function railSplitBounds(rowWidthPx: number): RailSplitBounds | null {
  const max = Math.min(RAIL_MAX_PX, Math.floor(rowWidthPx - GAP_PX - MIN_READABLE_CHART_PX));
  if (!Number.isFinite(max) || max <= RAIL_MIN_PX) return null;
  return { min: RAIL_MIN_PX, max };
}

/** The user's number, held inside the bounds. Rounded, because a fractional
 * pixel width on a flex child is a sub-pixel seam beside the divider. */
export function clampRailPx(railPx: number, bounds: RailSplitBounds): number {
  return Math.min(Math.max(Math.round(railPx), bounds.min), bounds.max);
}

/**
 * The rail width a pointer at `pointerX` is asking for.
 *
 * Measured from the row's RIGHT edge, because the rail is the right-hand pane
 * and its width is what the caller sets — `rowRight - pointerX` is the rail
 * plus half the divider, so half the divider's own width comes off. Anchoring
 * on the pointer rather than accumulating deltas is what keeps a drag that
 * leaves the window and comes back from arriving somewhere else than the
 * cursor.
 *
 * Unclamped on purpose: clamping is {@link clampRailPx}'s job and the caller
 * needs both — the raw ask decides nothing, but a divider that stops dead at a
 * bound while the cursor keeps going should stop at the bound, not follow a
 * pre-clamped ghost.
 */
export function railPxFromPointer(args: {
  pointerX: number;
  rowRight: number;
  dividerWidthPx: number;
}): number {
  return args.rowRight - args.pointerX - args.dividerWidthPx / 2;
}
